/**
 * HTTP client — thin fetch wrapper scoped to the orchestrator's REST surface.
 *
 * Covers auth + profile CRUD + backend lifecycle + backend logs. Grows as
 * more of the desktop IPC surface is ported to HTTP.
 */
import type {
	BackendLogSource,
	BackendSupervisorState,
} from "@shared/api/schemas/backend";
import type {
	CreateDesktopProfileInput,
	DesktopProfilesSnapshot,
	RenameDesktopProfileInput,
} from "@shared/api/schemas/profiles";
import type {
	LoginRequest,
	LoginResponse,
	SignupRequest,
} from "@shared/api/schemas/auth";
import type {
	AppSettings,
	AppSettingsSnapshot,
} from "@shared/api/schemas/settings";
import type {
	TelegramAuthInputStep,
	TelegramRegistrationInput,
	TelegramSessionState,
} from "@shared/api/schemas/telegram";
import type {
	Mt5RuntimeStatus,
	Mt5RuntimeStatusReport,
} from "@shared/api/schemas/mt5-runtime";
// Re-export for SPA components that import these types from this client.
// (Single import point for the SPA's REST + types surface.)
export type { Mt5RuntimeStatus, Mt5RuntimeStatusReport };
import { jwtStore } from "./jwt-store";

const API_BASE =
	(import.meta.env.VITE_SIGNALLENS_API_BASE as string | undefined) ?? "";

export class ApiError extends Error {
	readonly status: number;
	readonly code?: string;
	constructor(status: number, message: string, code?: string) {
		super(message);
		this.name = "ApiError";
		this.status = status;
		this.code = code;
	}
}

async function jsonFetch<T>(
	path: string,
	init: RequestInit & { auth?: boolean } = {},
): Promise<T> {
	const headers: Record<string, string> = {
		"Content-Type": "application/json",
		...(init.headers as Record<string, string> | undefined),
	};
	if (init.auth ?? true) {
		const session = jwtStore.get();
		if (session) headers.Authorization = `Bearer ${session.token}`;
	}
	const res = await fetch(`${API_BASE}${path}`, { ...init, headers });
	const text = await res.text();
	const body: unknown = text ? safeJson(text) : undefined;
	if (!res.ok) {
		if (res.status === 401) {
			jwtStore.clear();
		}
		const err = body as
			| { error?: { code?: string; message?: string } }
			| undefined;
		throw new ApiError(
			res.status,
			err?.error?.message ?? `request failed (${res.status})`,
			err?.error?.code,
		);
	}
	return body as T;
}

function safeJson(text: string): unknown {
	try {
		return JSON.parse(text);
	} catch {
		return undefined;
	}
}

export interface UserVisibilityFlags {
	showLogsCard?: boolean;
	showPlaygroundCard?: boolean;
	showMt5ConnectionCard?: boolean;
}

export interface AdminUserView {
	id: string;
	email: string;
	role: "user" | "admin";
	emailVerifiedAt: string | null;
	createdAt: string;
	// `false` until the user consumes their invite token. Surfaces the
	// "Resend invite" affordance in the admin modal.
	passwordSet: boolean;
	visibilityFlags: UserVisibilityFlags;
}

export interface CreateAdminUserInput {
	email: string;
	role: "user" | "admin";
}

export interface ResetTokenCheck {
	valid: boolean;
	email?: string;
	kind?: "invite" | "reset";
}

export const httpClient = {
	auth: {
		setupRequired(): Promise<{ required: boolean }> {
			return jsonFetch<{ required: boolean }>("/api/v1/auth/setup-required", {
				auth: false,
			});
		},
		signup(body: SignupRequest): Promise<LoginResponse> {
			return jsonFetch<LoginResponse>("/api/v1/auth/signup", {
				method: "POST",
				body: JSON.stringify(body),
				auth: false,
			});
		},
		login(body: LoginRequest): Promise<LoginResponse> {
			return jsonFetch<LoginResponse>("/api/v1/auth/login", {
				method: "POST",
				body: JSON.stringify(body),
				auth: false,
			});
		},
		async logout(): Promise<void> {
			const session = jwtStore.get();
			if (!session) return;
			await fetch(`${API_BASE}/api/v1/auth/logout`, {
				method: "POST",
				headers: { Authorization: `Bearer ${session.token}` },
			}).catch(() => void 0);
			jwtStore.clear();
		},
		passwordReset: {
			request(email: string): Promise<{ ok: true }> {
				return jsonFetch<{ ok: true }>(
					"/api/v1/auth/password-reset/request",
					{
						method: "POST",
						body: JSON.stringify({ email }),
						auth: false,
					},
				);
			},
			check(token: string): Promise<ResetTokenCheck> {
				return jsonFetch<ResetTokenCheck>(
					`/api/v1/auth/password-reset/check?token=${encodeURIComponent(token)}`,
					{ auth: false },
				);
			},
			consume(token: string, password: string): Promise<{ ok: true }> {
				return jsonFetch<{ ok: true }>(
					"/api/v1/auth/password-reset/consume",
					{
						method: "POST",
						body: JSON.stringify({ token, password }),
						auth: false,
					},
				);
			},
		},
	},

	admin: {
		users: {
			list(): Promise<AdminUserView[]> {
				return jsonFetch<AdminUserView[]>("/api/v1/admin/users");
			},
			create(input: CreateAdminUserInput): Promise<AdminUserView> {
				return jsonFetch<AdminUserView>("/api/v1/admin/users", {
					method: "POST",
					body: JSON.stringify(input),
				});
			},
			resendInvite(id: string): Promise<void> {
				return jsonFetch<void>(
					`/api/v1/admin/users/${encodeURIComponent(id)}/resend-invite`,
					{ method: "POST" },
				);
			},
			setRole(
				id: string,
				role: "user" | "admin",
			): Promise<AdminUserView> {
				return jsonFetch<AdminUserView>(
					`/api/v1/admin/users/${encodeURIComponent(id)}/role`,
					{ method: "PATCH", body: JSON.stringify({ role }) },
				);
			},
			setDisabled(id: string, disabled: boolean): Promise<AdminUserView> {
				return jsonFetch<AdminUserView>(
					`/api/v1/admin/users/${encodeURIComponent(id)}/disabled`,
					{ method: "PATCH", body: JSON.stringify({ disabled }) },
				);
			},
			setVisibilityFlags(
				id: string,
				flags: UserVisibilityFlags,
			): Promise<AdminUserView> {
				return jsonFetch<AdminUserView>(
					`/api/v1/admin/users/${encodeURIComponent(id)}/visibility-flags`,
					{ method: "PATCH", body: JSON.stringify(flags) },
				);
			},
			delete(id: string): Promise<void> {
				return jsonFetch<void>(
					`/api/v1/admin/users/${encodeURIComponent(id)}`,
					{ method: "DELETE" },
				);
			},
		},
	},

	profiles: {
		list(): Promise<DesktopProfilesSnapshot> {
			return jsonFetch<DesktopProfilesSnapshot>("/api/v1/profiles");
		},
		create(input: CreateDesktopProfileInput): Promise<DesktopProfilesSnapshot> {
			return jsonFetch<DesktopProfilesSnapshot>("/api/v1/profiles", {
				method: "POST",
				body: JSON.stringify(input),
			});
		},
		rename(
			profileId: string,
			name: string,
		): Promise<DesktopProfilesSnapshot> {
			const payload: Pick<RenameDesktopProfileInput, "name"> = { name };
			return jsonFetch<DesktopProfilesSnapshot>(
				`/api/v1/profiles/${encodeURIComponent(profileId)}`,
				{ method: "PATCH", body: JSON.stringify(payload) },
			);
		},
		delete(profileId: string): Promise<DesktopProfilesSnapshot> {
			return jsonFetch<DesktopProfilesSnapshot>(
				`/api/v1/profiles/${encodeURIComponent(profileId)}`,
				{ method: "DELETE" },
			);
		},
		rotateMt5Key(profileId: string): Promise<{ mt5ApiKey: string }> {
			return jsonFetch<{ mt5ApiKey: string }>(
				`/api/v1/profiles/${encodeURIComponent(profileId)}/mt5-key/rotate`,
				{ method: "POST" },
			);
		},
	},

	settings: {
		get(profileId: string): Promise<AppSettingsSnapshot> {
			return jsonFetch<AppSettingsSnapshot>(
				`/api/v1/profiles/${encodeURIComponent(profileId)}/settings`,
			);
		},
		save(
			profileId: string,
			patch: Partial<AppSettings>,
		): Promise<AppSettingsSnapshot> {
			return jsonFetch<AppSettingsSnapshot>(
				`/api/v1/profiles/${encodeURIComponent(profileId)}/settings`,
				{ method: "PUT", body: JSON.stringify(patch) },
			);
		},
		exportProfile(
			profileId: string,
			channelChatIds: number[] = [],
		): Promise<{
			version: number;
			exportedAt: string;
			profileName: string;
			channels: unknown[];
		}> {
			return jsonFetch(
				`/api/v1/profiles/${encodeURIComponent(profileId)}/settings/export`,
				{ method: "POST", body: JSON.stringify({ channelChatIds }) },
			);
		},
		importProfile(
			profileId: string,
			payload: { channels: unknown[]; mode?: "merge" | "replace" },
		): Promise<AppSettingsSnapshot> {
			return jsonFetch(
				`/api/v1/profiles/${encodeURIComponent(profileId)}/settings/import`,
				{ method: "POST", body: JSON.stringify(payload) },
			);
		},
	},

	telegram: {
		getSession(profileId: string): Promise<TelegramSessionState> {
			return jsonFetch<TelegramSessionState>(
				`/api/v1/profiles/${encodeURIComponent(profileId)}/telegram/session`,
			);
		},
		submitAuth(
			profileId: string,
			step: TelegramAuthInputStep,
			value: string,
		): Promise<TelegramSessionState> {
			return jsonFetch<TelegramSessionState>(
				`/api/v1/profiles/${encodeURIComponent(profileId)}/telegram/auth`,
				{ method: "POST", body: JSON.stringify({ step, value }) },
			);
		},
		submitRegistration(
			profileId: string,
			input: TelegramRegistrationInput,
		): Promise<TelegramSessionState> {
			return jsonFetch<TelegramSessionState>(
				`/api/v1/profiles/${encodeURIComponent(profileId)}/telegram/registration`,
				{ method: "POST", body: JSON.stringify(input) },
			);
		},
		logout(profileId: string): Promise<TelegramSessionState> {
			return jsonFetch<TelegramSessionState>(
				`/api/v1/profiles/${encodeURIComponent(profileId)}/telegram/logout`,
				{ method: "POST" },
			);
		},
		refreshChannels(profileId: string): Promise<TelegramSessionState> {
			return jsonFetch<TelegramSessionState>(
				`/api/v1/profiles/${encodeURIComponent(profileId)}/telegram/channels/refresh`,
				{ method: "POST" },
			);
		},
	},

	tradeCases: {
		list(
			profileId: string,
			query?: { limit?: number; offset?: number },
		): Promise<{ items: unknown[]; total?: number }> {
			const qs = new URLSearchParams();
			if (query?.limit) qs.set("limit", String(query.limit));
			if (query?.offset) qs.set("offset", String(query.offset));
			const suffix = qs.toString() ? `?${qs}` : "";
			return jsonFetch(
				`/api/v1/profiles/${encodeURIComponent(profileId)}/trade-cases${suffix}`,
			);
		},
		getStats(profileId: string): Promise<unknown> {
			return jsonFetch(
				`/api/v1/profiles/${encodeURIComponent(profileId)}/trade-cases/stats`,
			);
		},
	},

	backend: {
		getState(profileId: string): Promise<BackendSupervisorState> {
			return jsonFetch<BackendSupervisorState>(
				`/api/v1/profiles/${encodeURIComponent(profileId)}/backend`,
			);
		},
		start(profileId: string): Promise<BackendSupervisorState> {
			return jsonFetch<BackendSupervisorState>(
				`/api/v1/profiles/${encodeURIComponent(profileId)}/backend/start`,
				{ method: "POST" },
			);
		},
		stop(profileId: string): Promise<BackendSupervisorState> {
			return jsonFetch<BackendSupervisorState>(
				`/api/v1/profiles/${encodeURIComponent(profileId)}/backend/stop`,
				{ method: "POST" },
			);
		},
		restart(profileId: string): Promise<BackendSupervisorState> {
			return jsonFetch<BackendSupervisorState>(
				`/api/v1/profiles/${encodeURIComponent(profileId)}/backend/restart`,
				{ method: "POST" },
			);
		},
		getLogSources(profileId: string): Promise<BackendLogSource[]> {
			return jsonFetch<BackendLogSource[]>(
				`/api/v1/profiles/${encodeURIComponent(profileId)}/backend/log-sources`,
			);
		},
		getLogs(profileId: string, limit = 200): Promise<string[]> {
			return jsonFetch<string[]>(
				`/api/v1/profiles/${encodeURIComponent(profileId)}/backend/logs?limit=${limit}`,
			);
		},
		clearLogs(profileId: string): Promise<void> {
			return jsonFetch<void>(
				`/api/v1/profiles/${encodeURIComponent(profileId)}/backend/logs/clear`,
				{ method: "POST" },
			);
		},
	},

	// MT5 runtime — per-profile MT5 pod managed by the orchestrator.
	// Credentials are ephemeral: the SPA submits them once on `start`,
	// they live only inside the resulting k8s Secret + the running pod,
	// and `stop` deletes them. The form is hidden whenever a pod is
	// running, so a browser refresh while running just shows status —
	// no creds need to round-trip. A refresh while NOT running shows
	// an empty form by design (the form's state is in-memory only).
	mt5Runtime: {
		async start(
			profileId: string,
			creds: Mt5RuntimeStartInput,
		): Promise<Mt5RuntimeStartResponse> {
			return jsonFetch<Mt5RuntimeStartResponse>(
				`/api/v1/profiles/${encodeURIComponent(profileId)}/mt5-runtime/start`,
				{ method: "POST", body: JSON.stringify(creds) },
			);
		},
		async stop(profileId: string): Promise<void> {
			await jsonFetch<void>(
				`/api/v1/profiles/${encodeURIComponent(profileId)}/mt5-runtime`,
				{ method: "DELETE" },
			);
		},
		getStatus(profileId: string): Promise<Mt5RuntimeStatusReport> {
			return jsonFetch<Mt5RuntimeStatusReport>(
				`/api/v1/profiles/${encodeURIComponent(profileId)}/mt5-runtime/status`,
			);
		},

		// Admin-only — UI must hide these unless `session.user.role === "admin"`.
		// The orchestrator also gates them server-side (RolesGuard +
		// @Roles("admin")), so a non-admin who hits them directly gets 403.
		getLogs(profileId: string, tail = 500): Promise<{ logs: string }> {
			return jsonFetch<{ logs: string }>(
				`/api/v1/profiles/${encodeURIComponent(profileId)}/mt5-runtime/logs?tail=${tail}`,
			);
		},
		expertAdvisorDownloadUrl(profileId: string): string {
			return `${API_BASE}/api/v1/profiles/${encodeURIComponent(profileId)}/mt5-runtime/expert-advisor`;
		},
		async downloadExpertAdvisor(profileId: string): Promise<void> {
			const session = jwtStore.get();
			if (!session) throw new ApiError(401, "not authenticated");
			const res = await fetch(this.expertAdvisorDownloadUrl(profileId), {
				headers: { Authorization: `Bearer ${session.token}` },
			});
			if (!res.ok) {
				throw new ApiError(res.status, `download failed (${res.status})`);
			}
			const blob = await res.blob();
			const url = URL.createObjectURL(blob);
			const a = document.createElement("a");
			a.href = url;
			a.download = `signallens-${profileId.slice(0, 8)}.mq5`;
			document.body.appendChild(a);
			a.click();
			document.body.removeChild(a);
			URL.revokeObjectURL(url);
		},
	},
};

// Mirrors server-orchestrator/src/mt5-runtime/mt5-runtime.service.ts.

export interface Mt5RuntimeStartInput {
	login: string;
	password: string;
	server: string;
	brokerName: string;
}

export interface Mt5RuntimeStartResponse {
	status: "accepted";
	pollUrl: string;
}
